import {
	AppBar,
	Divider,
	Grid,
	IconButton,
	Toolbar,
	Typography,
	Alert,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	Card,
	CardContent,
	Tab,
	Tabs,
	ButtonGroup,
	CircularProgress,
	Chip,
	useMediaQuery,
	useTheme,
} from '@mui/material'
import Transition from '../../../components/Transition'
import CloseIcon from '@mui/icons-material/Close'
import PhonelinkEraseIcon from '@mui/icons-material/PhonelinkErase'
import { Box } from '@mui/system'
import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import SummarizeIcon from '@mui/icons-material/Summarize'
import InsightsIcon from '@mui/icons-material/Insights'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import { useState, useEffect } from 'react'
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	BarElement,
	Title,
	Tooltip,
	Legend,
} from 'chart.js'
import { Line, Bar } from 'react-chartjs-2'
import dayjs from 'dayjs'
import DnsIcon from '@mui/icons-material/Dns'
import Accordion, {
	AccordionSlots,
	accordionClasses,
} from '@mui/material/Accordion'
import AccordionSummary from '@mui/material/AccordionSummary'
import AccordionDetails, {
	accordionDetailsClasses,
} from '@mui/material/AccordionDetails'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Fade from '@mui/material/Fade'
import { apiHealthDataDetail, apiUserSummaryByUserId } from 'api/api'
import { HealthDataDetailRequestType } from 'constants/appType'

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	BarElement,
	Title,
	Tooltip,
	Legend,
)

type UserDataProps = {
	dialogOpen: boolean
	dialogClose: () => void
	userId: number | null
}

interface TabPanelProps {
	children?: React.ReactNode
	index: number
	value: number
}

// 일별 건강 데이터 타입 (API 응답)
interface DailyHealthData {
	regDt: string
	statDate: string
	avgBpm: number
	maxBpm: number
	minBpm: number
	avgBpSys: number
	maxBpSys: number
	avgBpDia: number
	avgBodyTemp: number
	maxBodyTemp: number
	avgSpo2: number
	totalStepCount: number
	totalSleepMin: number
	warningCount?: number
	dangerCount?: number
}

// 차트용 데이터 아이템
interface ChartDataItem {
	label: string
	value: number
	value2?: number // 혈압 최대값, 심박 최대값용
	value3?: number // 심박 최소값용
	unit: string
	statDate: string
	isToday: boolean
}

// 인사이트 타입
type InsightType = 'good' | 'warning' | 'bad' | 'info'

// 인사이트 모델
interface HealthInsight {
	type: InsightType
	text: string
}

// 요약 데이터
interface SummaryData {
	bestDay: string
	needImprovement: string
	achievementRate: string
}

// AI 콜 상세 메시지 타입
interface CallDetail {
	callId: number
	createdAt: string
	messageText: string
	msgId: number
	msgOrder: number
	speakerType: string
}

// AI 콜 데이터 타입
interface CallData {
	callId: number
	callStatus: string
	callStatusText: string
	callType: string
	details: CallDetail[] | null
	durationSec: number
	endDt: string
	moodStatus: string
	regDt: string
	startDt: string
	summaryText: string
	userId: number
}

interface phoneCallConditions {
	startTime: string
}
interface SummaryUserInfo {
	userId: number
	deviceId: string
	deviceName: string
	deviceVersion: string
	parentName: string
	parentPhone: string
	seniorName: string
	seniorPhone: string
	birthYear: number
	phoneCallConditions: phoneCallConditions[]
}

function CustomTabPanel(props: TabPanelProps) {
	const { children, value, index, ...other } = props

	return (
		<div
			role="tabpanel"
			hidden={value !== index}
			id={`simple-tabpanel-${index}`}
			aria-labelledby={`simple-tab-${index}`}
			{...other}
		>
			{value === index && (
				<Box sx={{ px: { xs: 0, md: 3 }, py: 3 }}>{children}</Box>
			)}
		</div>
	)
}

function a11yProps(index: number) {
	return {
		id: `simple-tab-${index}`,
		'aria-controls': `simple-tabpanel-${index}`,
	}
}

// 숫자 3자리 쉼표 포맷
const formatNumber = (number: number): string => {
	return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

// 인사이트 아이콘 반환
const getInsightIcon = (type: InsightType) => {
	switch (type) {
		case 'good':
			return <CheckCircleOutlineIcon sx={{ color: '#4CAF50' }} />
		case 'warning':
			return <WarningAmberIcon sx={{ color: '#FF9800' }} />
		case 'bad':
			return <ErrorOutlineIcon sx={{ color: '#E53935' }} />
		case 'info':
			return <InfoOutlinedIcon sx={{ color: '#42A5F5' }} />
	}
}

// 인사이트 색상 반환
const getInsightColor = (type: InsightType): string => {
	switch (type) {
		case 'good':
			return '#4CAF50'
		case 'warning':
			return '#FF9800'
		case 'bad':
			return '#E53935'
		case 'info':
			return '#42A5F5'
	}
}

// 인사이트 생성
const generateInsights = (
	healthDataList: DailyHealthData[],
): HealthInsight[] => {
	const insights: HealthInsight[] = []
	if (healthDataList.length === 0) {
		insights.push({ type: 'info', text: '분석할 데이터가 충분하지 않습니다' })
		return insights
	}

	// 심박수 분석
	const bpmList = healthDataList.filter((d) => d.avgBpm > 0).map((d) => d.avgBpm)
	if (bpmList.length > 0) {
		const avgBpm = bpmList.reduce((a, b) => a + b, 0) / bpmList.length
		if (avgBpm >= 60 && avgBpm <= 100) {
			insights.push({
				type: 'good',
				text: `심박수: 평균 ${avgBpm.toFixed(1)}bpm으로 정상 범위`,
			})
		} else if (avgBpm < 60) {
			insights.push({
				type: 'warning',
				text: `심박수: 평균 ${avgBpm.toFixed(1)}bpm으로 낮은 편`,
			})
		} else {
			insights.push({
				type: 'warning',
				text: `심박수: 평균 ${avgBpm.toFixed(1)}bpm으로 높은 편`,
			})
		}
	}

	// 혈압 분석
	const bpSysList = healthDataList
		.filter((d) => d.avgBpSys > 0)
		.map((d) => d.avgBpSys)
	if (bpSysList.length > 0) {
		const avgSys = bpSysList.reduce((a, b) => a + b, 0) / bpSysList.length
		if (avgSys >= 90 && avgSys <= 120) {
			insights.push({
				type: 'good',
				text: `혈압: 수축기 평균 ${avgSys.toFixed(1)}mmHg로 정상`,
			})
		} else if (avgSys > 140) {
			insights.push({
				type: 'bad',
				text: `혈압: 수축기 평균 ${avgSys.toFixed(1)}mmHg로 고혈압 주의`,
			})
		} else if (avgSys > 120) {
			insights.push({
				type: 'warning',
				text: `혈압: 수축기 평균 ${avgSys.toFixed(1)}mmHg로 약간 높음`,
			})
		}
	}

	// 체온 분석
	const tempList = healthDataList
		.filter((d) => d.avgBodyTemp > 0)
		.map((d) => d.avgBodyTemp)
	if (tempList.length > 0) {
		const avgTemp = tempList.reduce((a, b) => a + b, 0) / tempList.length
		if (avgTemp >= 36.1 && avgTemp <= 37.2) {
			insights.push({
				type: 'good',
				text: `체온: 평균 ${avgTemp.toFixed(1)}°C로 정상`,
			})
		} else if (avgTemp > 37.5) {
			insights.push({
				type: 'bad',
				text: `체온: 평균 ${avgTemp.toFixed(1)}°C로 발열 의심`,
			})
		} else if (avgTemp > 37.2) {
			insights.push({
				type: 'warning',
				text: `체온: 평균 ${avgTemp.toFixed(1)}°C로 약간 높음`,
			})
		}
	}

	// 활동량 분석
	const stepList = healthDataList
		.filter((d) => d.totalStepCount > 0)
		.map((d) => d.totalStepCount)
	if (stepList.length > 0) {
		const avgSteps = stepList.reduce((a, b) => a + b, 0) / stepList.length
		const formattedSteps = formatNumber(Math.round(avgSteps))
		if (avgSteps >= 8000) {
			insights.push({
				type: 'good',
				text: `활동량: 일평균 ${formattedSteps}걸음으로 양호`,
			})
		} else if (avgSteps >= 5000) {
			insights.push({
				type: 'warning',
				text: `활동량: 일평균 ${formattedSteps}걸음, 조금 더 움직여보세요`,
			})
		} else if (avgSteps > 0) {
			insights.push({
				type: 'bad',
				text: `활동량: 일평균 ${formattedSteps}걸음으로 부족`,
			})
		}
	}

	// 수면 분석
	const sleepList = healthDataList
		.filter((d) => d.totalSleepMin > 0)
		.map((d) => d.totalSleepMin)
	if (sleepList.length > 0) {
		const avgSleepMin = sleepList.reduce((a, b) => a + b, 0) / sleepList.length
		const avgSleepHour = avgSleepMin / 60
		if (avgSleepHour >= 7 && avgSleepHour <= 9) {
			insights.push({
				type: 'good',
				text: `수면: 일평균 ${avgSleepHour.toFixed(1)}시간으로 적정`,
			})
		} else if (avgSleepHour < 6) {
			insights.push({
				type: 'bad',
				text: `수면: 일평균 ${avgSleepHour.toFixed(1)}시간으로 부족`,
			})
		} else if (avgSleepHour < 7) {
			insights.push({
				type: 'warning',
				text: `수면: 일평균 ${avgSleepHour.toFixed(1)}시간으로 약간 부족`,
			})
		} else {
			insights.push({
				type: 'warning',
				text: `수면: 일평균 ${avgSleepHour.toFixed(1)}시간으로 많은 편`,
			})
		}
	}

	if (insights.length === 0) {
		insights.push({ type: 'info', text: '분석할 데이터가 충분하지 않습니다' })
	}

	return insights
}

// 요약 데이터 생성
const generateSummary = (healthDataList: DailyHealthData[]): SummaryData => {
	if (healthDataList.length === 0) {
		return { bestDay: '-', needImprovement: '-', achievementRate: '-' }
	}

	const weekdays = ['일', '월', '화', '수', '목', '금', '토']

	// 최고의 날 찾기 (종합 점수가 가장 높은 날)
	const dailyScores: { [key: string]: number } = {}
	const dailyDates: { [key: string]: Date } = {}

	for (const item of healthDataList) {
		const statDate = new Date(item.statDate)
		const dayKey = `${statDate.getMonth() + 1}/${statDate.getDate()}`
		dailyDates[dayKey] = statDate

		let score = 0
		let count = 0

		// 심박수 점수 (60-100 정상)
		if (item.avgBpm > 0) {
			if (item.avgBpm >= 60 && item.avgBpm <= 100) score += 100
			else if (item.avgBpm >= 50 && item.avgBpm <= 110) score += 70
			else score += 40
			count++
		}

		// 혈압 점수 (수축기 90-120 정상)
		if (item.avgBpSys > 0) {
			if (item.avgBpSys >= 90 && item.avgBpSys <= 120) score += 100
			else if (item.avgBpSys >= 80 && item.avgBpSys <= 140) score += 70
			else score += 40
			count++
		}

		// 체온 점수 (36.1-37.2 정상)
		if (item.avgBodyTemp > 0) {
			if (item.avgBodyTemp >= 36.1 && item.avgBodyTemp <= 37.2) score += 100
			else if (item.avgBodyTemp >= 35.5 && item.avgBodyTemp <= 37.5) score += 70
			else score += 40
			count++
		}

		// 수면 점수 (7-9시간 정상)
		if (item.totalSleepMin > 0) {
			const sleepHour = item.totalSleepMin / 60
			if (sleepHour >= 7 && sleepHour <= 9) score += 100
			else if (sleepHour >= 6 && sleepHour <= 10) score += 70
			else score += 40
			count++
		}

		if (count > 0) {
			const avgScore = score / count
			dailyScores[dayKey] = (dailyScores[dayKey] || 0) + avgScore
		}
	}

	// 최고의 날 찾기
	let bestDay = '-'
	if (Object.keys(dailyScores).length > 0) {
		const bestDayKey = Object.entries(dailyScores).reduce((a, b) =>
			a[1] > b[1] ? a : b,
		)[0]
		const bestDate = dailyDates[bestDayKey]
		bestDay = `${bestDayKey} (${weekdays[bestDate.getDay()]})`
	}

	// 개선 필요 항목 찾기
	const metricScores: { name: string; score: number }[] = []

	const bpmList = healthDataList.filter((d) => d.avgBpm > 0).map((d) => d.avgBpm)
	if (bpmList.length > 0) {
		const avgBpm = bpmList.reduce((a, b) => a + b, 0) / bpmList.length
		const bpmScore =
			avgBpm >= 60 && avgBpm <= 100 ? 100 : avgBpm >= 50 && avgBpm <= 110 ? 70 : 40
		metricScores.push({ name: '심박수', score: bpmScore })
	}

	const bpList = healthDataList
		.filter((d) => d.avgBpSys > 0)
		.map((d) => d.avgBpSys)
	if (bpList.length > 0) {
		const avgBp = bpList.reduce((a, b) => a + b, 0) / bpList.length
		const bpScore =
			avgBp >= 90 && avgBp <= 120 ? 100 : avgBp >= 80 && avgBp <= 140 ? 70 : 40
		metricScores.push({ name: '혈압', score: bpScore })
	}

	const tempList = healthDataList
		.filter((d) => d.avgBodyTemp > 0)
		.map((d) => d.avgBodyTemp)
	if (tempList.length > 0) {
		const avgTemp = tempList.reduce((a, b) => a + b, 0) / tempList.length
		const tempScore =
			avgTemp >= 36.1 && avgTemp <= 37.2
				? 100
				: avgTemp >= 35.5 && avgTemp <= 37.5
					? 70
					: 40
		metricScores.push({ name: '체온', score: tempScore })
	}

	const stepList = healthDataList
		.filter((d) => d.totalStepCount > 0)
		.map((d) => d.totalStepCount)
	if (stepList.length > 0) {
		const avgSteps = stepList.reduce((a, b) => a + b, 0) / stepList.length
		const stepScore = avgSteps >= 8000 ? 100 : avgSteps >= 5000 ? 70 : 40
		metricScores.push({ name: '활동량', score: stepScore })
	}

	const sleepList = healthDataList
		.filter((d) => d.totalSleepMin > 0)
		.map((d) => d.totalSleepMin)
	if (sleepList.length > 0) {
		const avgSleepMin = sleepList.reduce((a, b) => a + b, 0) / sleepList.length
		const sleepHour = avgSleepMin / 60
		const sleepScore =
			sleepHour >= 7 && sleepHour <= 9
				? 100
				: sleepHour >= 6 && sleepHour <= 10
					? 70
					: 40
		metricScores.push({ name: '수면', score: sleepScore })
	}

	let needImprovement = '-'
	let achievementRate = '-'

	if (metricScores.length > 0) {
		metricScores.sort((a, b) => a.score - b.score)
		needImprovement = metricScores[0].name

		// 달성률 계산 (전체 평균 점수)
		const totalScore =
			metricScores.map((m) => m.score).reduce((a, b) => a + b, 0) /
			metricScores.length
		achievementRate = `${totalScore.toFixed(0)}%`
	}

	return { bestDay, needImprovement, achievementRate }
}

// 차트 데이터 생성 함수
const createChartData = (
	healthDataList: DailyHealthData[],
	dataType:
		| 'heartRate'
		| 'bloodPressure'
		| 'bloodOxygen'
		| 'temperature'
		| 'sleep'
		| 'activity',
): ChartDataItem[] => {
	const weekdays = ['일', '월', '화', '수', '목', '금', '토']
	const today = dayjs()
	const chartData: ChartDataItem[] = []

	for (const item of healthDataList) {
		const statDate = dayjs(item.statDate)
		const dayKey = statDate.format('M/D')
		const isToday = statDate.isSame(today, 'day')

		let value = 0
		let unit = ''

		switch (dataType) {
			case 'heartRate':
				value = item.avgBpm
				unit = 'bpm'
				// value2, value3는 아래에서 별도 처리
				break
			case 'bloodPressure':
				value = item.avgBpSys
				unit = 'mmHg'
				// value2는 아래에서 별도 처리
				break
			case 'bloodOxygen':
				value = item.avgSpo2
				unit = '%'
				break
			case 'temperature':
				value = item.avgBodyTemp
				unit = '°C'
				// value2는 아래에서 별도 처리
				break
			case 'sleep':
				value = item.totalSleepMin / 60 // 시간으로 변환
				unit = '시간'
				break
			case 'activity':
				value = item.totalStepCount
				unit = '걸음'
				break
		}

		if (value > 0) {
			const chartItem: ChartDataItem = {
				label: weekdays[statDate.day()],
				value,
				unit,
				statDate: dayKey,
				isToday,
			}
			// 심박인 경우 최대/최소값도 추가
			if (dataType === 'heartRate') {
				if (item.maxBpm > 0) chartItem.value2 = item.maxBpm
				if (item.minBpm > 0) chartItem.value3 = item.minBpm
			}
			// 혈압인 경우 최대값도 추가
			if (dataType === 'bloodPressure' && item.maxBpSys > 0) {
				chartItem.value2 = item.maxBpSys
			}
			// 체온인 경우 최대값도 추가
			if (dataType === 'temperature' && item.maxBodyTemp > 0) {
				chartItem.value2 = item.maxBodyTemp
			}
			chartData.push(chartItem)
		}
	}

	return chartData
}

const UserData = ({ dialogOpen, dialogClose, userId }: UserDataProps) => {
	const theme = useTheme()
	const fullScreen = useMediaQuery(theme.breakpoints.down('md'))

	const [tabValue, setTabValue] = useState(0)
	const [loading, setLoading] = useState(false)
	const [healthDataList, setHealthDataList] = useState<DailyHealthData[]>([])
	const [insights, setInsights] = useState<HealthInsight[]>([])
	const [summary, setSummary] = useState<SummaryData>({
		bestDay: '-',
		needImprovement: '-',
		achievementRate: '-',
	})
	const [selectedDateText, setSelectedDateText] = useState<string>('7')
	const [expanded, setExpanded] = useState(false)
	const [summaryUserInfo, setSummaryUserInfo] = useState<SummaryUserInfo | null>(
		null,
	)
	const [callDataList, setCallDataList] = useState<CallData[]>([])

	const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
		setTabValue(newValue)
	}

	const handleExpansion = () => {
		setExpanded((prevExpanded) => !prevExpanded)
	}

	// 기간 변경 시 데이터 조회
	const fetchHealthData = (days: number) => {
		if (!userId) return

		setLoading(true)
		const startDate = dayjs().subtract(days, 'day').format('YYYY-MM-DD 00:00:00')
		const endDate = dayjs().format('YYYY-MM-DD 23:59:59')
		const request: HealthDataDetailRequestType = {
			id: userId,
			startDate: startDate,
			endDate: endDate,
		}

		apiHealthDataDetail(request)
			.then((res) => {
				console.log('상세 데이터:', res)
				const dailyData = res.dailyData || []
				const callData = res.callData || []
				setHealthDataList(dailyData)
				setCallDataList(callData)
				// 인사이트 및 요약 생성
				setInsights(generateInsights(dailyData))
				setSummary(generateSummary(dailyData))
			})
			.catch((err) => {
				console.error('건강 데이터 조회 실패:', err)
			})
			.finally(() => {
				setLoading(false)
			})

		// 사용자 요약 정보 조회
		apiUserSummaryByUserId(userId)
			.then((res) => {
				console.log('사용자 요약 정보:', res)
				setSummaryUserInfo(res)
			})
			.catch((err) => {
				console.error('사용자 요약 정보 조회 실패:', err)
			})
	}

	// 다이얼로그 열릴 때 데이터 조회 (기본 7일) 및 탭 초기화
	useEffect(() => {
		if (dialogOpen && userId) {
			setTabValue(0) // 탭 초기화
			setSelectedDateText('7')
			fetchHealthData(7)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [dialogOpen, userId])

	// 기간 변경 핸들러
	const handlePeriodChange = (period: string) => {
		setSelectedDateText(period)
		fetchHealthData(parseInt(period))
	}

	// 현재 탭의 차트 데이터 가져오기
	const getCurrentChartData = () => {
		const dataTypeMap: {
			[key: number]:
				| 'heartRate'
				| 'bloodPressure'
				| 'bloodOxygen'
				| 'temperature'
				| 'sleep'
				| 'activity'
		} = {
			0: 'heartRate',
			1: 'bloodPressure',
			2: 'bloodOxygen',
			3: 'temperature',
			4: 'sleep',
			5: 'activity',
		}
		return createChartData(healthDataList, dataTypeMap[tabValue])
	}

	// 차트 데이터 형식 변환
	const getChartJsData = (chartData: ChartDataItem[]) => {
		if (chartData.length === 0) {
			return {
				labels: [],
				datasets: [
					{
						label: '데이터',
						data: [],
						borderColor: 'rgb(75, 192, 192)',
						backgroundColor: 'rgba(75, 192, 192, 0.5)',
						tension: 0.3,
					},
				],
			}
		}

		const colorMap: { [key: number]: { border: string; bg: string } } = {
			0: { border: 'rgb(244, 67, 54)', bg: 'rgba(244, 67, 54, 0.5)' }, // 심박수
			1: { border: 'rgb(33, 150, 243)', bg: 'rgba(33, 150, 243, 0.5)' }, // 혈압
			2: { border: 'rgb(0, 188, 212)', bg: 'rgba(0, 188, 212, 0.5)' }, // 혈중 산소
			3: { border: 'rgb(255, 152, 0)', bg: 'rgba(255, 152, 0, 0.5)' }, // 체온
			4: { border: 'rgb(103, 58, 183)', bg: 'rgba(103, 58, 183, 0.5)' }, // 수면
			5: { border: 'rgb(76, 175, 80)', bg: 'rgba(76, 175, 80, 0.5)' }, // 활동량
		}

		// 심박 탭인 경우 평균/최대/최소 세 개의 라인 표시
		if (tabValue === 0) {
			return {
				labels: chartData.map((d) => d.statDate),
				datasets: [
					{
						label: '평균',
						data: chartData.map((d) => d.value),
						borderColor: 'rgb(33, 150, 243)',
						backgroundColor: 'rgba(33, 150, 243, 0.5)',
						tension: 0.3,
					},
					{
						label: '최대',
						data: chartData.map((d) => d.value2 || 0),
						borderColor: 'rgb(244, 67, 54)',
						backgroundColor: 'rgba(244, 67, 54, 0.5)',
						tension: 0.3,
					},
					{
						label: '최소',
						data: chartData.map((d) => d.value3 || 0),
						borderColor: 'rgb(76, 175, 80)',
						backgroundColor: 'rgba(76, 175, 80, 0.5)',
						tension: 0.3,
					},
				],
			}
		}

		// 혈압 탭인 경우 평균/최대 두 개의 라인 표시
		if (tabValue === 1) {
			return {
				labels: chartData.map((d) => d.statDate),
				datasets: [
					{
						label: '평균 혈압',
						data: chartData.map((d) => d.value),
						borderColor: 'rgb(33, 150, 243)',
						backgroundColor: 'rgba(33, 150, 243, 0.5)',
						tension: 0.3,
					},
					{
						label: '최대 혈압',
						data: chartData.map((d) => d.value2 || 0),
						borderColor: 'rgb(244, 67, 54)',
						backgroundColor: 'rgba(244, 67, 54, 0.5)',
						tension: 0.3,
					},
				],
			}
		}

		// 체온 탭인 경우 평균/최대 두 개의 라인 표시
		if (tabValue === 3) {
			return {
				labels: chartData.map((d) => d.statDate),
				datasets: [
					{
						label: '평균 체온',
						data: chartData.map((d) => d.value),
						borderColor: 'rgb(255, 152, 0)',
						backgroundColor: 'rgba(255, 152, 0, 0.5)',
						tension: 0.3,
					},
					{
						label: '최대 체온',
						data: chartData.map((d) => d.value2 || 0),
						borderColor: 'rgb(244, 67, 54)',
						backgroundColor: 'rgba(244, 67, 54, 0.5)',
						tension: 0.3,
					},
				],
			}
		}

		return {
			labels: chartData.map((d) => d.statDate),
			datasets: [
				{
					label: chartData[0]?.unit || '',
					data: chartData.map((d) => d.value),
					borderColor: colorMap[tabValue]?.border || 'rgb(75, 192, 192)',
					backgroundColor: colorMap[tabValue]?.bg || 'rgba(75, 192, 192, 0.5)',
					tension: 0.3,
				},
			],
		}
	}

	// 탭별 타이틀 및 단위
	const getTabInfo = () => {
		const tabInfo: { [key: number]: { title: string; unit: string } } = {
			0: { title: '심박수', unit: 'bpm' },
			1: { title: '혈압', unit: 'mmHg' },
			2: { title: '혈중 산소', unit: '%' },
			3: { title: '체온', unit: '°C' },
			4: { title: '수면', unit: '시간' },
			5: { title: '활동량', unit: '걸음' },
		}
		return tabInfo[tabValue]
	}

	// 통계 계산
	const getStatistics = (chartData: ChartDataItem[]) => {
		if (chartData.length === 0) {
			return {
				max: '-',
				min: '-',
				avg: '-',
				max2: '-',
				avg2: '-',
				max3: '-',
				avg3: '-',
			}
		}
		const values = chartData.map((d) => d.value)
		const max = Math.max(...values)
		const min = Math.min(...values)
		const avg = values.reduce((a, b) => a + b, 0) / values.length

		const unit = chartData[0]?.unit || ''
		const formatVal = (v: number) => {
			if (tabValue === 4) {
				// 수면
				const h = Math.floor(v)
				const m = Math.round((v - h) * 60)
				return m === 0 ? `${h}시간` : `${h}시간 ${m}분`
			}
			if (tabValue === 5) return formatNumber(Math.round(v)) + ' ' + unit // 활동량
			if (tabValue === 3) return v.toFixed(1) + unit // 체온
			return v.toFixed(0) + ' ' + unit
		}

		// 심박 탭인 경우 최대(value2), 최소(value3) 통계 계산
		let max2 = '-'
		let avg2 = '-'
		let max3 = '-'
		let avg3 = '-'
		if (tabValue === 0) {
			const values2 = chartData
				.filter((d) => d.value2 && d.value2 > 0)
				.map((d) => d.value2!)
			if (values2.length > 0) {
				const maxVal2 = Math.max(...values2)
				const avgVal2 = values2.reduce((a, b) => a + b, 0) / values2.length
				max2 = maxVal2.toFixed(0) + ' ' + unit
				avg2 = avgVal2.toFixed(0) + ' ' + unit
			}
			const values3 = chartData
				.filter((d) => d.value3 && d.value3 > 0)
				.map((d) => d.value3!)
			if (values3.length > 0) {
				const minVal3 = Math.min(...values3)
				const avgVal3 = values3.reduce((a, b) => a + b, 0) / values3.length
				max3 = minVal3.toFixed(0) + ' ' + unit
				avg3 = avgVal3.toFixed(0) + ' ' + unit
			}
		}

		// 혈압 탭인 경우 최대값(value2) 통계도 계산
		if (tabValue === 1) {
			const values2 = chartData
				.filter((d) => d.value2 && d.value2 > 0)
				.map((d) => d.value2!)
			if (values2.length > 0) {
				const maxVal2 = Math.max(...values2)
				const avgVal2 = values2.reduce((a, b) => a + b, 0) / values2.length
				max2 = maxVal2.toFixed(0) + ' ' + unit
				avg2 = avgVal2.toFixed(0) + ' ' + unit
			}
		}

		// 체온 탭인 경우 최대값(value2) 통계도 계산
		if (tabValue === 3) {
			const values2 = chartData
				.filter((d) => d.value2 && d.value2 > 0)
				.map((d) => d.value2!)
			if (values2.length > 0) {
				const maxVal2 = Math.max(...values2)
				const avgVal2 = values2.reduce((a, b) => a + b, 0) / values2.length
				max2 = maxVal2.toFixed(1) + unit
				avg2 = avgVal2.toFixed(1) + unit
			}
		}

		return {
			max: formatVal(max),
			min: formatVal(min),
			avg: formatVal(avg),
			max2,
			avg2,
			max3,
			avg3,
		}
	}

	const currentChartData = getCurrentChartData()
	const chartJsData = getChartJsData(currentChartData)
	const tabInfo = getTabInfo()
	const statistics = getStatistics(currentChartData)

	// 공통 탭 컨텐츠 렌더링
	const renderTabContent = () => {
		return (
			<>
				<Grid
					container
					spacing={2}
					sx={{ mb: 2 }}
				>
					<Grid size={{ xs: 12, md: 6 }}>
						<Typography>
							{selectedDateText}일 {tabInfo.title} ({tabInfo.unit})
						</Typography>
					</Grid>
					<Grid
						size={{ xs: 12, md: 6 }}
						sx={{ textAlign: 'right' }}
					>
						<ButtonGroup
							variant="outlined"
							aria-label="Basic button group"
						>
							{['1', '7', '30', '90'].map((period) => (
								<Button
									key={period}
									size="small"
									variant={selectedDateText === period ? 'contained' : 'outlined'}
									color="primary"
									onClick={() => handlePeriodChange(period)}
								>
									{period}일
								</Button>
							))}
						</ButtonGroup>
					</Grid>
				</Grid>
				{loading ? (
					<Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
						<CircularProgress />
					</Box>
				) : (
					<>
						<Box sx={{ height: 300 }}>
							{tabValue === 4 || tabValue === 5 ? (
								<Bar
									options={{
										responsive: true,
										maintainAspectRatio: false,
										plugins: {
											legend: {
												position: 'top' as const,
												display: false,
											},
											title: {
												display: false,
											},
										},
										scales: {
											y: {
												beginAtZero: true,
											},
										},
									}}
									data={chartJsData}
								/>
							) : (
								<Line
									options={{
										responsive: true,
										maintainAspectRatio: false,
										plugins: {
											legend: {
												position: 'top' as const,
												display: tabValue === 0 || tabValue === 1 || tabValue === 3, // 심박, 혈압, 체온 탭은 범례 표시
											},
											title: {
												display: false,
											},
										},
										scales: {
											y: {
												beginAtZero: false,
											},
										},
									}}
									data={chartJsData}
								/>
							)}
						</Box>
						{tabValue === 0 ? (
							// 심박 탭: 평균/최대/최소 구분하여 표시
							<Grid
								container
								spacing={2}
								sx={{ my: 2 }}
							>
								<Grid size={{ xs: 12, md: 4 }}>
									<Alert
										severity="info"
										icon={false}
									>
										<Typography variant="subtitle2">평균 심박수</Typography>
										<Typography variant="body2">
											최대: {statistics.max} / 평균: {statistics.avg}
										</Typography>
									</Alert>
								</Grid>
								<Grid size={{ xs: 12, md: 4 }}>
									<Alert
										severity="error"
										icon={false}
									>
										<Typography variant="subtitle2">최대 심박수</Typography>
										<Typography variant="body2">
											최대: {statistics.max2} / 평균: {statistics.avg2}
										</Typography>
									</Alert>
								</Grid>
								<Grid size={{ xs: 12, md: 4 }}>
									<Alert
										severity="success"
										icon={false}
									>
										<Typography variant="subtitle2">최소 심박수</Typography>
										<Typography variant="body2">
											최소: {statistics.max3} / 평균: {statistics.avg3}
										</Typography>
									</Alert>
								</Grid>
							</Grid>
						) : tabValue === 1 ? (
							// 혈압 탭: 평균/최대 구분하여 표시
							<Grid
								container
								spacing={2}
								sx={{ my: 2 }}
							>
								<Grid size={{ xs: 12, md: 6 }}>
									<Alert
										severity="info"
										icon={false}
									>
										<Typography variant="subtitle2">평균 혈압</Typography>
										<Typography variant="body2">
											최대: {statistics.max} / 평균: {statistics.avg}
										</Typography>
									</Alert>
								</Grid>
								<Grid size={{ xs: 12, md: 6 }}>
									<Alert
										severity="error"
										icon={false}
									>
										<Typography variant="subtitle2">최대 혈압</Typography>
										<Typography variant="body2">
											최대: {statistics.max2} / 평균: {statistics.avg2}
										</Typography>
									</Alert>
								</Grid>
							</Grid>
						) : tabValue === 3 ? (
							// 체온 탭: 평균/최대 구분하여 표시
							<Grid
								container
								spacing={2}
								sx={{ my: 2 }}
							>
								<Grid size={{ xs: 12, md: 6 }}>
									<Alert
										severity="warning"
										icon={false}
									>
										<Typography variant="subtitle2">평균 체온</Typography>
										<Typography variant="body2">
											최대: {statistics.max} / 평균: {statistics.avg}
										</Typography>
									</Alert>
								</Grid>
								<Grid size={{ xs: 12, md: 6 }}>
									<Alert
										severity="error"
										icon={false}
									>
										<Typography variant="subtitle2">최대 체온</Typography>
										<Typography variant="body2">
											최대: {statistics.max2} / 평균: {statistics.avg2}
										</Typography>
									</Alert>
								</Grid>
							</Grid>
						) : (
							<Grid
								container
								spacing={2}
								sx={{ my: 2 }}
							>
								<Grid size={{ xs: 12, md: 4 }}>
									<Alert severity="success">최대 {statistics.max}</Alert>
								</Grid>
								<Grid size={{ xs: 12, md: 4 }}>
									<Alert severity="info">최소 {statistics.min}</Alert>
								</Grid>
								<Grid size={{ xs: 12, md: 4 }}>
									<Alert severity="warning">평균 {statistics.avg}</Alert>
								</Grid>
							</Grid>
						)}
						{/* 표준 수치 안내 */}
						<Card
							variant="outlined"
							sx={{ my: 2 }}
						>
							<CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}
								>
									<InsightsIcon sx={{ verticalAlign: 'middle', mr: 1 }} /> 표준 수치 안내
									(시니어 기준)
								</Typography>
								<Grid
									container
									spacing={1}
								>
									<Grid size={{ xs: 6, md: 4 }}>
										<Typography
											variant="caption"
											color="text.secondary"
										>
											심박수: 60-100 bpm
										</Typography>
									</Grid>
									<Grid size={{ xs: 6, md: 4 }}>
										<Typography
											variant="caption"
											color="text.secondary"
										>
											혈압: 90-120 / 60-80 mmHg
										</Typography>
									</Grid>
									<Grid size={{ xs: 6, md: 4 }}>
										<Typography
											variant="caption"
											color="text.secondary"
										>
											혈중산소: 95-100%
										</Typography>
									</Grid>
									<Grid size={{ xs: 6, md: 4 }}>
										<Typography
											variant="caption"
											color="text.secondary"
										>
											체온: 36.1-37.2°C
										</Typography>
									</Grid>
									<Grid size={{ xs: 6, md: 4 }}>
										<Typography
											variant="caption"
											color="text.secondary"
										>
											수면: 7-8시간
										</Typography>
									</Grid>
									<Grid size={{ xs: 6, md: 4 }}>
										<Typography
											variant="caption"
											color="text.secondary"
										>
											활동량: 7,000-10,000보
										</Typography>
									</Grid>
								</Grid>
							</CardContent>
						</Card>
						<Divider sx={{ my: 2 }} />
						<Typography variant="body1">연계 정보</Typography>
						<Alert
							severity="warning"
							sx={{ mt: 2 }}
						>
							AI 콜 기록
						</Alert>
						{callDataList.length > 0 ? (
							callDataList.map((callItem, index) => (
								<Accordion key={callItem.callId}>
									<AccordionSummary
										expandIcon={<ExpandMoreIcon />}
										aria-controls={`panel${index}-content`}
										id={`panel${index}-header`}
									>
										<Grid
											container
											spacing={1}
											sx={{ width: '100%' }}
										>
											<Grid size={{ xs: 12, md: 3, lg: 2 }}>
												{dayjs(callItem.startDt).format('YYYY-MM-DD')}
											</Grid>
											<Grid size={{ xs: 12, md: 3, lg: 2 }}>{callItem.callType}</Grid>
											<Grid size={{ xs: 12, md: 6, lg: 8 }}>
												통화 내용 요약 : {callItem.summaryText || '-'}
											</Grid>
										</Grid>
									</AccordionSummary>
									<AccordionDetails>
										{callItem.details && callItem.details.length > 0 ? (
											<Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
												{callItem.details.map((detail) => (
													<Box
														key={detail.msgId}
														sx={{
															display: 'flex',
															justifyContent:
																detail.speakerType === '시스템'
																	? 'center'
																	: detail.speakerType === 'AI'
																		? 'flex-start'
																		: 'flex-end',
														}}
													>
														<Box
															sx={{
																maxWidth: detail.speakerType === '시스템' ? '90%' : '75%',
																p: 1.5,
																borderRadius: detail.speakerType === '시스템' ? 2 : 2,
																bgcolor:
																	detail.speakerType === '시스템'
																		? 'error.light'
																		: detail.speakerType === 'AI'
																			? 'grey.100'
																			: 'primary.main',
																color:
																	detail.speakerType === '시스템'
																		? 'error.contrastText'
																		: detail.speakerType === 'AI'
																			? 'text.primary'
																			: 'primary.contrastText',
																...(detail.speakerType !== '시스템' && {
																	borderTopLeftRadius: detail.speakerType === 'AI' ? 0 : 16,
																	borderTopRightRadius: detail.speakerType === 'AI' ? 16 : 0,
																}),
																textAlign: detail.speakerType === '시스템' ? 'center' : 'left',
															}}
														>
															<Typography
																variant="caption"
																sx={{
																	display: 'block',
																	mb: 0.5,
																	color:
																		detail.speakerType === '시스템'
																			? 'rgba(255,255,255,0.8)'
																			: detail.speakerType === 'AI'
																				? 'text.secondary'
																				: 'rgba(255,255,255,0.7)',
																}}
															>
																[{detail.speakerType}] {detail.createdAt}
															</Typography>
															<Typography variant="body2">{detail.messageText}</Typography>
														</Box>
													</Box>
												))}
											</Box>
										) : (
											<Typography color="text.secondary">통화 내용 없음</Typography>
										)}
									</AccordionDetails>
								</Accordion>
							))
						) : (
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ py: 2, textAlign: 'center' }}
							>
								AI 콜 기록이 없습니다.
							</Typography>
						)}
					</>
				)}
			</>
		)
	}

	return (
		<Dialog
			open={dialogOpen}
			onClose={dialogClose}
			slots={{
				transition: Transition,
			}}
			maxWidth="lg"
			fullScreen={fullScreen}
		>
			<AppBar sx={{ position: 'sticky' }}>
				<Toolbar>
					<Typography
						sx={{ flex: 1 }}
						variant="h6"
						component="div"
					>
						{summaryUserInfo ? summaryUserInfo.seniorName : '사용자'} 님 건강 데이터
					</Typography>
					<IconButton
						edge="end"
						color="inherit"
						onClick={dialogClose}
						aria-label="close"
					>
						<CloseIcon />
					</IconButton>
				</Toolbar>
			</AppBar>
			<DialogContent>
				<Grid
					container
					spacing={2}
				>
					<Grid size={{ xs: 12, md: 4 }}>
						{/* 사용자 정보 카드 */}
						<Card
							elevation={3}
							sx={{
								borderRadius: 3,
								background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
								color: 'white',
							}}
						>
							<CardContent>
								<Box
									sx={{
										display: 'flex',
										flexDirection: 'column',
										alignItems: 'center',
										mb: 2,
									}}
								>
									<AccountCircleIcon sx={{ fontSize: 80, mb: 1, opacity: 0.9 }} />
									<Typography
										variant="h5"
										fontWeight="bold"
									>
										{summaryUserInfo ? summaryUserInfo.seniorName : ''}
									</Typography>
								</Box>
								<Divider sx={{ my: 2, borderColor: 'rgba(255,255,255,0.3)' }} />
								<Grid
									container
									spacing={1.5}
								>
									<Grid size={5}>
										<Typography
											variant="body2"
											sx={{ opacity: 0.8 }}
										>
											나이
										</Typography>
									</Grid>
									<Grid size={7}>
										<Typography
											variant="body2"
											fontWeight="medium"
										>
											{summaryUserInfo
												? `${summaryUserInfo.birthYear}년 (${new Date().getFullYear() - summaryUserInfo.birthYear}세)`
												: ''}
										</Typography>
									</Grid>
									<Grid size={5}>
										<Typography
											variant="body2"
											sx={{ opacity: 0.8 }}
										>
											연락처
										</Typography>
									</Grid>
									<Grid size={7}>
										<Typography
											variant="body2"
											fontWeight="medium"
										>
											{summaryUserInfo ? summaryUserInfo.seniorPhone : ''}
										</Typography>
									</Grid>
									<Grid size={5}>
										<Typography
											variant="body2"
											sx={{ opacity: 0.8 }}
										>
											보호자 정보
										</Typography>
									</Grid>
									<Grid size={7}>
										<Typography
											variant="body2"
											fontWeight="medium"
										>
											{summaryUserInfo
												? `${summaryUserInfo.parentName} (${summaryUserInfo.parentPhone})`
												: ''}
										</Typography>
									</Grid>
									<Grid size={5}>
										<Typography
											variant="body2"
											sx={{ opacity: 0.8 }}
										>
											디바이스
										</Typography>
									</Grid>
									<Grid size={7}>
										<Typography
											variant="body2"
											fontWeight="medium"
										>
											{summaryUserInfo ? summaryUserInfo.deviceName : ''}
											{summaryUserInfo?.deviceName && summaryUserInfo?.deviceVersion
												? ' / '
												: ''}
											{summaryUserInfo ? summaryUserInfo.deviceVersion : ''}
										</Typography>
									</Grid>
									<Grid size={5}>
										<Typography
											variant="body2"
											sx={{ opacity: 0.8 }}
										>
											통화가능 시간
										</Typography>
									</Grid>
									<Grid size={7}>
										{summaryUserInfo?.phoneCallConditions &&
											summaryUserInfo?.phoneCallConditions.map(
												(item: phoneCallConditions, index: number) => (
													<Typography
														key={index}
														variant="body2"
														fontWeight="medium"
													>
														{item.startTime}
													</Typography>
												),
											)}
									</Grid>
								</Grid>
							</CardContent>
						</Card>

						{/* 요약 카드 */}
						<Card
							variant="outlined"
							sx={{ mt: 2 }}
						>
							<CardContent>
								<Typography
									variant="subtitle1"
									fontWeight="bold"
									gutterBottom
									sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
								>
									<SummarizeIcon color="primary" /> {selectedDateText}일 요약
								</Typography>
								<Grid
									container
									spacing={1}
									sx={{ mt: 1 }}
								>
									<Grid size={6}>
										<Typography
											variant="body2"
											color="text.secondary"
										>
											최고의 날
										</Typography>
									</Grid>
									<Grid size={6}>
										<Typography
											variant="body2"
											fontWeight="medium"
										>
											{summary.bestDay}
										</Typography>
									</Grid>
									<Grid size={6}>
										<Typography
											variant="body2"
											color="text.secondary"
										>
											개선 필요
										</Typography>
									</Grid>
									<Grid size={6}>
										<Typography
											variant="body2"
											fontWeight="medium"
										>
											{summary.needImprovement}
										</Typography>
									</Grid>
									<Grid size={6}>
										<Typography
											variant="body2"
											color="text.secondary"
										>
											건강 달성률
										</Typography>
									</Grid>
									<Grid size={6}>
										<Typography
											variant="body2"
											fontWeight="medium"
										>
											{summary.achievementRate}
										</Typography>
									</Grid>
								</Grid>
							</CardContent>
						</Card>

						{/* 인사이트 카드 */}
						<Card
							variant="outlined"
							sx={{ mt: 2 }}
						>
							<CardContent>
								<Typography
									variant="subtitle1"
									fontWeight="bold"
									gutterBottom
									sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
								>
									<DnsIcon color="primary" /> {selectedDateText}일 건강 인사이트
								</Typography>
								<Box sx={{ mt: 1 }}>
									{insights.map((insight, index) => (
										<Box
											key={index}
											sx={{
												display: 'flex',
												alignItems: 'flex-start',
												gap: 1,
												mb: 1,
												p: 1,
												borderRadius: 1,
												bgcolor: `${getInsightColor(insight.type)}10`,
											}}
										>
											{getInsightIcon(insight.type)}
											<Typography
												variant="body2"
												sx={{ color: getInsightColor(insight.type) }}
											>
												{insight.text}
											</Typography>
										</Box>
									))}
								</Box>
							</CardContent>
						</Card>

						{/* 건강 상태 카드 */}
						<Card
							variant="outlined"
							sx={{ mt: 2 }}
						>
							<CardContent sx={{ textAlign: 'center' }}>
								{healthDataList[0] ? (
									<>
										{healthDataList[0].dangerCount &&
										healthDataList[0].dangerCount > 0 ? (
											<Chip
												label="위험"
												color="error"
												size="medium"
												sx={{ fontSize: '1rem', py: 2 }}
											/>
										) : healthDataList[0].warningCount &&
										  healthDataList[0].warningCount > 0 ? (
											<Chip
												label="주의"
												color="warning"
												size="medium"
												sx={{ fontSize: '1rem', py: 2 }}
											/>
										) : (
											<Chip
												label="정상"
												color="success"
												size="medium"
												sx={{ fontSize: '1rem', py: 2 }}
											/>
										)}
									</>
								) : (
									<Chip
										label="알수없음"
										color="default"
										size="medium"
										sx={{ fontSize: '1rem', py: 2 }}
									/>
								)}
								<Typography
									variant="body2"
									color="text.secondary"
									sx={{ mt: 2 }}
								>
									마지막 업데이트:{' '}
									{healthDataList[0]?.regDt
										? dayjs(healthDataList[0].regDt).format('YYYY-MM-DD HH시')
										: '-'}
								</Typography>
								{/* <Button
									variant="outlined"
									size="small"
									startIcon={<SyncIcon />}
								>
									동기화
								</Button> */}
							</CardContent>
						</Card>
					</Grid>

					<Grid size={{ xs: 12, md: 8 }}>
						<Box sx={{ width: '100%' }}>
							<Box>
								<Tabs
									value={tabValue}
									onChange={handleTabChange}
									indicatorColor="secondary"
									textColor="inherit"
									aria-label="health data tabs"
									variant="scrollable"
									scrollButtons="auto"
								>
									<Tab
										label="심박수"
										{...a11yProps(0)}
									/>
									<Tab
										label="혈압"
										{...a11yProps(1)}
									/>
									<Tab
										label="혈중 산소"
										{...a11yProps(2)}
									/>
									<Tab
										label="체온"
										{...a11yProps(3)}
									/>
									<Tab
										label="수면"
										{...a11yProps(4)}
									/>
									<Tab
										label="활동량"
										{...a11yProps(5)}
									/>
								</Tabs>
							</Box>
							<CustomTabPanel
								value={tabValue}
								index={0}
							>
								{renderTabContent()}
							</CustomTabPanel>
							<CustomTabPanel
								value={tabValue}
								index={1}
							>
								{renderTabContent()}
							</CustomTabPanel>
							<CustomTabPanel
								value={tabValue}
								index={2}
							>
								{renderTabContent()}
							</CustomTabPanel>
							<CustomTabPanel
								value={tabValue}
								index={3}
							>
								{renderTabContent()}
							</CustomTabPanel>
							<CustomTabPanel
								value={tabValue}
								index={4}
							>
								{renderTabContent()}
							</CustomTabPanel>
							<CustomTabPanel
								value={tabValue}
								index={5}
							>
								{renderTabContent()}
							</CustomTabPanel>
						</Box>
					</Grid>
				</Grid>
			</DialogContent>
			<Divider />
			<DialogActions>
				<Button
					variant="contained"
					onClick={dialogClose}
					color="primary"
					size="small"
					startIcon={<PhonelinkEraseIcon />}
				>
					닫기
				</Button>
			</DialogActions>
		</Dialog>
	)
}

export default UserData
